/**
 * =============================================================================
 * PAGE HEADER COMPONENT - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 * 
 * Consistent page header across all views.
 *
 * DESIGN SYSTEM:
 * - Page title: 28px semibold
 * - Subtitle: 14px, muted
 * - 8pt spacing grid
 *
 * Sizing lives in PageHeader.module.css, which steps the header down on short
 * viewports (Windows laptops at 125-150% display scaling) so the full-height
 * content below keeps its rows. See the module for the breakpoints.
 */

import { Box, Group, Stack, Text } from '@mantine/core';
import { memo, type FC, type ReactNode } from 'react';

import { spacing, neutralColors } from '@styles/design-system';

import classes from './PageHeader.module.css';

export interface PageHeaderProps {
    /** Page title - 28px */
    title: string;
    /** Optional subtitle - 14px muted */
    subtitle?: string;
    /** Optional icon (emoji or component) */
    icon?: ReactNode;
    /** Optional override for title color */
    titleColor?: string;
    /** Right-side action buttons */
    actions?: ReactNode;
    /**
     * Extra class on the root, for pages that need to tune the space beneath the header.
     *
     * The default 32px suits a page whose next element is a dense grid. A page that opens on
     * a one-line banner does not need it, and on a short viewport that gap is the difference
     * between seeing the first job and scrolling for it.
     */
    className?: string;
}

const PageHeader: FC<PageHeaderProps> = memo(({
    title,
    subtitle,
    icon,
    titleColor,
    actions,
    className,
}) => {
    return (
        <Box className={className ? `${classes.root} ${className}` : classes.root}>
            <Group justify="space-between" align="flex-end" className={classes.bar}>
                <Group style={{ gap: spacing.sm }} className={classes.titleGroup}>
                    {/* Icon */}
                    {icon && <Box className={classes.icon}>{icon}</Box>}

                    <Stack className={classes.text}>
                        {/* Page Title - 28px */}
                        <Text
                            className={classes.title}
                            c={titleColor ?? neutralColors.textPrimary}
                        >
                            {title}
                        </Text>

                        {/* Subtitle - 14px muted */}
                        {subtitle && (
                            <Text
                                className={classes.subtitle}
                                c={neutralColors.textSecondary}
                            >
                                {subtitle}
                            </Text>
                        )}
                    </Stack>
                </Group>
                
                {/* Actions */}
                {actions && (
                    <Group style={{ gap: spacing.sm }}>
                        {actions}
                    </Group>
                )}
            </Group>
        </Box>
    );
});

PageHeader.displayName = 'PageHeader';

export default PageHeader;
